import { Link } from "react-router-dom";
import { ArrowLeft, FileText, ExternalLink } from "lucide-react";
import { CASE_STUDIES as D } from "@/data/caseStudies";

function ext(url) {
  return { href: url, target: "_blank", rel: "noopener noreferrer" };
}

function StudyCard({ title, desc, url }) {
  return (
    <a
      {...ext(url)}
      className="glass-card group block p-6 hover:bg-[#00F0FF]/5 transition-colors"
    >
      <div className="flex items-start gap-4">
        <span className="w-10 h-10 grid place-items-center border border-[#00F0FF]/40 bg-[#00F0FF]/10 flex-shrink-0 group-hover:bg-[#00F0FF] transition-colors">
          <FileText
            className="w-4 h-4 text-[#00F0FF] group-hover:text-black transition-colors"
            strokeWidth={2.2}
          />
        </span>
        <div className="flex-1 min-w-0">
          <h3 className="font-display font-bold text-base sm:text-lg leading-tight">
            {title}
          </h3>
          {desc && (
            <p className="mt-3 text-white/70 text-sm leading-relaxed">{desc}</p>
          )}
          <div className="mt-4 inline-flex items-center gap-2 font-mono-ui text-[10px] tracking-[0.3em] uppercase text-[#00F0FF] group-hover:text-white transition-colors">
            Learn more <ExternalLink className="w-3 h-3" />
          </div>
        </div>
      </div>
    </a>
  );
}

function Section({ id, label, title, children }) {
  return (
    <section id={id} className="relative py-16 lg:py-20">
      <div className="mb-8">
        {label && <span className="text-label">∙ {label}</span>}
        <h2 className="mt-3 font-display font-black uppercase tracking-tighter text-3xl sm:text-4xl lg:text-5xl leading-[0.95]">
          {title}
        </h2>
      </div>
      {children}
    </section>
  );
}

export default function CaseStudiesPage() {
  return (
    <div className="relative min-h-screen text-white bg-[#030508]">
      {/* DNA video background (shared aesthetic) */}
      <div className="pointer-events-none fixed inset-0 z-0 overflow-hidden bg-[#030508]">
        <video
          autoPlay
          loop
          muted
          playsInline
          preload="auto"
          className="absolute inset-0 w-full h-full object-cover dna-flow"
          style={{ filter: "saturate(1.4) brightness(1.3) contrast(1.05)" }}
        >
          <source src="/assets/dna-bg.webm" type="video/webm" />
          <source src="/assets/dna-bg.mp4" type="video/mp4" />
        </video>
        <div
          className="absolute inset-0"
          style={{
            background:
              "linear-gradient(180deg, rgba(3,5,8,0.55) 0%, rgba(3,5,8,0.7) 50%, rgba(3,5,8,0.8) 100%)",
          }}
        />
      </div>

      {/* Header */}
      <header className="relative z-20 border-b border-[#00F0FF]/15 bg-[#030508]/80 backdrop-blur-xl">
        <div className="max-w-5xl mx-auto px-6 lg:px-10 py-5 flex items-center justify-between">
          <Link
            to="/"
            data-testid="case-studies-back"
            className="inline-flex items-center gap-2 font-mono-ui text-[11px] tracking-[0.25em] uppercase text-white/70 hover:text-[#00F0FF] transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to TBT
          </Link>
          <span className="text-label">Case Studies / X39</span>
        </div>
      </header>

      <main className="relative z-10 max-w-5xl mx-auto px-6 lg:px-10 pb-24 pt-12">
        {/* Intro */}
        <section className="py-10">
          <span className="text-label">∙ Science · Research</span>
          <h1 className="mt-4 font-display font-black uppercase tracking-tighter text-4xl sm:text-5xl lg:text-6xl leading-[0.95]">
            {D.intro.title}
          </h1>
          <p className="mt-8 text-white/80 text-base sm:text-lg leading-relaxed max-w-3xl">
            {D.intro.tagline}
          </p>
          <p className="mt-6 text-white/70 leading-relaxed max-w-3xl">
            {D.intro.extra}
          </p>
        </section>

        {/* Early Studies */}
        <Section id="early-studies" label="Early Studies" title="X39 Studies A & B">
          <div className="grid sm:grid-cols-2 gap-5">
            {D.earlyStudies.map((s) => (
              <StudyCard key={s.title} {...s} />
            ))}
          </div>
        </Section>

        {/* Phototherapy */}
        <Section id="phototherapy" label="Phototherapy" title={D.phototherapy.title}>
          <div className="space-y-4 text-white/75 leading-relaxed max-w-3xl">
            {D.phototherapy.body.map((p, i) => (
              <p key={i}>{p}</p>
            ))}
          </div>
          <div className="mt-6 grid sm:grid-cols-2 gap-3">
            {D.phototherapy.links.map((l) => (
              <a
                key={l.url}
                {...ext(l.url)}
                className="glass-card flex items-center justify-between gap-4 px-5 py-4 hover:bg-[#00F0FF]/5 transition-colors"
              >
                <span className="font-display font-bold">{l.label}</span>
                <ExternalLink className="w-4 h-4 text-[#00F0FF]" />
              </a>
            ))}
          </div>
        </Section>

        {/* Acupressure */}
        <Section id="acupressure" label="Acupressure" title={D.acupressure.title}>
          <div className="space-y-4 text-white/75 leading-relaxed max-w-3xl">
            {D.acupressure.body.map((p, i) => (
              <p key={i}>{p}</p>
            ))}
          </div>
          <div className="mt-6 grid sm:grid-cols-2 gap-3">
            {D.acupressure.links.map((l) => (
              <a
                key={l.url}
                {...ext(l.url)}
                className="glass-card flex items-center justify-between gap-4 px-5 py-4 hover:bg-[#00F0FF]/5 transition-colors"
              >
                <span className="font-display font-bold">{l.label}</span>
                <ExternalLink className="w-4 h-4 text-[#00F0FF]" />
              </a>
            ))}
          </div>
        </Section>

        {/* Patents */}
        <Section id="patents" label="Patents" title={D.patents.title}>
          <div className="space-y-4 text-white/75 leading-relaxed max-w-3xl mb-8">
            {D.patents.body.map((p, i) => (
              <p key={i} className={i === 2 ? "font-mono-ui text-xs tracking-[0.15em] uppercase text-[#00F0FF]/80" : ""}>{p}</p>
            ))}
          </div>
          <div className="grid sm:grid-cols-2 gap-5">
            {D.patents.items.map((s) => (
              <StudyCard key={s.title} {...s} />
            ))}
          </div>
        </Section>

        {/* Product Research */}
        <Section id="product-research" label="Our Product Research" title={D.research.title}>
          <div className="space-y-4 text-white/75 leading-relaxed max-w-3xl mb-8">
            {D.research.body.map((p, i) => (
              <p key={i}>{p}</p>
            ))}
          </div>
          <div className="grid sm:grid-cols-2 gap-5">
            {D.research.studies.map((s) => (
              <StudyCard key={s.title} {...s} />
            ))}
          </div>
        </Section>

        {/* Acupressure Info */}
        <Section id="acupressure-info" label="Information on Acupressure" title={D.acupressureInfo.title}>
          <div className="grid sm:grid-cols-2 gap-5">
            {D.acupressureInfo.items.map((s) => (
              <StudyCard key={s.title} {...s} />
            ))}
          </div>
        </Section>

        {/* Safety */}
        <Section id="safety" label="Mechanisms & Safety" title={D.safety.title}>
          <div className="grid sm:grid-cols-2 gap-5">
            {D.safety.items.map((s) => (
              <StudyCard key={s.title} {...s} />
            ))}
          </div>
        </Section>

        {/* CEO */}
        <Section id="ceo" label="Leadership" title={D.ceo.title}>
          <div className="space-y-4 text-white/75 leading-relaxed max-w-3xl">
            {D.ceo.bio.map((p, i) => (
              <p key={i}>{p}</p>
            ))}
          </div>
          <div className="mt-6 grid sm:grid-cols-2 gap-3">
            {D.ceo.links.map((l) => (
              <a
                key={l.url}
                {...ext(l.url)}
                className="glass-card flex items-center justify-between gap-4 px-5 py-4 hover:bg-[#00F0FF]/5 transition-colors"
              >
                <span className="font-display font-bold">{l.label}</span>
                <ExternalLink className="w-4 h-4 text-[#00F0FF]" />
              </a>
            ))}
          </div>
        </Section>

        {/* Disclaimer */}
        <section className="mt-16 pt-8 border-t border-white/10">
          <p className="font-mono-ui text-[10px] tracking-[0.15em] uppercase text-white/40 leading-loose">
            {D.disclaimer}
          </p>
        </section>
      </main>
    </div>
  );
}
